/**
 * useDashboardStats — Hook for Dashboard Home KPIs, chart data, and activity feed
 *
 * Fetches all data needed by DashboardHome in a single hook:
 * - KPI stats (total employees, active now, avg wellness, alerts)
 * - Wellness chart time-series
 * - Recent activity feed
 *
 * Auto-refreshes on a 30-second interval.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchDashboardKPIs,
  fetchWellnessChartData,
  fetchRecentActivity,
  type DashboardKPIs,
  type WellnessChartDataPoint,
  type RecentActivityItem,
} from "../lib/supabase";

const REFRESH_INTERVAL_MS = 30_000; // 30s auto-refresh

export interface DashboardStats {
  kpis: DashboardKPIs;
  chartData: WellnessChartDataPoint[];
  recentActivity: RecentActivityItem[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refresh: () => Promise<void>;
}

export function useDashboardStats(
  organizationId: string,
  chartDays: number = 30,
): DashboardStats {
  const [kpis, setKpis] = useState<DashboardKPIs>({
    totalEmployees: 0,
    activeNow: 0,
    avgWellnessScore: 0,
    activeAlerts: 0,
    avgPosture: 70,
    avgFatigue: 0,
    highFatigueCount: 0,
    poorPostureCount: 0,
    wellnessTrend: 0,
    alertsTrend: 0,
  });
  const [chartData, setChartData] = useState<WellnessChartDataPoint[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivityItem[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Ref to track mount state
  const mountedRef = useRef(true);
  const chartDaysRef = useRef(chartDays);
  chartDaysRef.current = chartDays;

  const fetchAll = useCallback(async () => {
    if (!organizationId) return;

    try {
      // Parallel fetch all dashboard data
      const [kpiData, chartResult, activityResult] = await Promise.all([
        fetchDashboardKPIs(organizationId),
        fetchWellnessChartData(organizationId, chartDaysRef.current),
        fetchRecentActivity(organizationId, 10),
      ]);

      if (!mountedRef.current) return;

      setKpis(kpiData);
      setChartData(chartResult);
      setRecentActivity(activityResult);
      setError(null);
      setLastUpdated(new Date());
    } catch (err: any) {
      if (!mountedRef.current) return;
      console.error("[useDashboardStats] Error:", err);
      setError(err.message || "Failed to load dashboard data");
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [organizationId]);

  // Initial fetch + auto-refresh
  useEffect(() => {
    mountedRef.current = true;
    setIsLoading(true);
    fetchAll();

    const interval = setInterval(fetchAll, REFRESH_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [fetchAll]);

  // Re-fetch when chart days change
  useEffect(() => {
    if (!isLoading) {
      fetchWellnessChartData(organizationId, chartDays).then((data) => {
        if (mountedRef.current) setChartData(data);
      });
    }
  }, [chartDays, organizationId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    kpis,
    chartData,
    recentActivity,
    isLoading,
    error,
    lastUpdated,
    refresh: fetchAll,
  };
}
